import type { TaskDecisionPrompt } from '@wix/ai-scheduling-assistant-common-lib/utils';

// The OPEN ASKS the account-wide `/api/tasks` read carries beside its rows (`openAsks` + `openAsksTotal`):
// everything waiting on the user that no task row and no outreach question carries — open goal / meeting /
// chat-only Decisions (answerable in place, `prompt`) and the notification ledger's live `held-send:` / `card:` /
// `conv:` NEEDS_YOU asks (`title` / `body`, the push copy). Built server-side by common-lib `readOpenAsks`; the
// Tasks screen's Action needed tab lists them so an "Action needed" push always leads to a row. Pure, no runtime
// imports, so it is testable without the app's graph.

/** One ask as the wire carries it (common-lib `OpenAsk`, restated structurally). */
export interface WebOpenAsk {
  /** `decision:<decisionId>` or the ledger key. */
  id: string;
  /** The conversation the ask was put in — what "Open conversation" opens. */
  conversationId: string;
  taskId?: string;
  goalId?: string;
  /** ISO. */
  createdDate?: string;
  prompt?: TaskDecisionPrompt;
  title?: string;
  body?: string;
  /** A chaser OWED item (`chase:<itemId>`) — common-lib `OpenAskChase`, restated. */
  chase?: WebChase;
}

export type ChaseStakes = 'critical' | 'high' | 'normal' | 'low';
export type ChaseCategory = 'money' | 'health' | 'people' | 'work' | 'other';
export type ChaseSource = 'gmail' | 'calendar' | 'file';

export interface WebChase {
  itemId: string;
  stakes: ChaseStakes;
  category: ChaseCategory;
  /** ISO. */
  dueAt?: string;
  source?: ChaseSource;
  verifiedSender?: string;
  /** A task the agent ran to PREPARE the action (a draft, a payment page, a slot); its approval Decision lives on
   *  that task, so the item page opens it ("Review what I prepared"). Absent on older servers. */
  preparedTaskId?: string;
  /** That approval's Decision id. Absent on older servers. */
  decisionId?: string;
}

export interface OpenAsksRead {
  asks: WebOpenAsk[];
  /** How many COUNT toward Action needed in all (a chase ask below high stakes never does); the list may be capped
   *  below it. Absent → the list's own counted length. */
  total?: number;
}

const STAKES = new Set<string>(['critical', 'high', 'normal', 'low']);
const CATEGORIES = new Set<string>(['money', 'health', 'people', 'work', 'other']);
const SOURCES = new Set<string>(['gmail', 'calendar', 'file']);

/** Whether an ask counts toward "Action needed": every ask but a chase item below high stakes (common-lib's rule). */
export function countsTowardActionNeeded(ask: Pick<WebOpenAsk, 'chase'>): boolean {
  return !ask.chase || ask.chase.stakes === 'critical' || ask.chase.stakes === 'high';
}

/** The chase items the owner closed in this page session. A refresh that raced the close can still carry one, and it
 *  must not come back; the server forgets it on its own a moment later. */
const closedChaseItems = new Set<string>();
export function markChaseItemClosed(itemId: string): void {
  closedChaseItems.add(itemId);
}
/** A close that failed: the row may come back. */
export function unmarkChaseItemClosed(itemId: string): void {
  closedChaseItems.delete(itemId);
}
export function isChaseItemClosed(itemId: string): boolean {
  return closedChaseItems.has(itemId);
}

/** The other asks the owner closed in this page session — "Not for me", or a reply typed on the row — by ask id
 *  (the ledger key). Same reason as the chase memory: a refresh that raced the close can still carry the row. */
const closedAsks = new Set<string>();
export function markAskClosed(id: string): void {
  closedAsks.add(id);
}
/** A close that failed: the row may come back. */
export function unmarkAskClosed(id: string): void {
  closedAsks.delete(id);
}

/** A wire `chase` read defensively: a malformed one drops the ask (it would be a row whose actions name nothing). */
function parseChase(raw: unknown): WebChase | null | undefined {
  if (raw === undefined) return undefined;
  const c = raw as Record<string, unknown> | null;
  if (!c || typeof c !== 'object' || !nonEmpty(c.itemId)) return null;
  const dueAt = nonEmpty(c.dueAt) && !Number.isNaN(Date.parse(c.dueAt)) ? c.dueAt : undefined;
  return {
    itemId: c.itemId,
    stakes: (typeof c.stakes === 'string' && STAKES.has(c.stakes) ? c.stakes : 'normal') as ChaseStakes,
    category: (typeof c.category === 'string' && CATEGORIES.has(c.category) ? c.category : 'other') as ChaseCategory,
    ...(dueAt ? { dueAt } : {}),
    ...(typeof c.source === 'string' && SOURCES.has(c.source) ? { source: c.source as ChaseSource } : {}),
    ...(nonEmpty(c.verifiedSender) ? { verifiedSender: c.verifiedSender } : {}),
    ...(nonEmpty(c.preparedTaskId) ? { preparedTaskId: c.preparedTaskId } : {}),
    ...(nonEmpty(c.decisionId) ? { decisionId: c.decisionId } : {}),
  };
}

const nonEmpty = (value: unknown): value is string => typeof value === 'string' && value.length > 0;

/** The two wire fields, read defensively: absent → `null` (the server could not look, or predates them); an entry
 *  with no `id` or `conversationId` is dropped — it would be a row with nowhere to open. */
export function parseOpenAsks(raw: unknown, total: unknown): OpenAsksRead | null {
  if (!Array.isArray(raw)) return null;
  const asks: WebOpenAsk[] = [];
  // A counted chase ask the owner closed this session still sits in the server's total until the server catches up.
  let closedCounted = 0;
  for (const entry of raw) {
    const candidate = entry as { id?: unknown; conversationId?: unknown; prompt?: { decisionId?: unknown }; chase?: unknown } | null;
    if (!candidate || typeof candidate !== 'object' || !nonEmpty(candidate.id) || !nonEmpty(candidate.conversationId)) continue;
    if (candidate.prompt !== undefined && !nonEmpty(candidate.prompt?.decisionId)) continue;
    const chase = parseChase(candidate.chase);
    if (chase === null) continue;
    if (chase && closedChaseItems.has(chase.itemId)) {
      if (countsTowardActionNeeded({ chase })) closedCounted++;
      continue;
    }
    if (!chase && closedAsks.has(candidate.id)) {
      closedCounted++;
      continue;
    }
    const { chase: _drop, ...rest } = candidate as WebOpenAsk;
    void _drop;
    asks.push(chase ? { ...rest, chase } : (rest as WebOpenAsk));
  }
  return typeof total === 'number' && Number.isFinite(total) ? { asks, total: Math.max(0, total - closedCounted) } : { asks };
}

/**
 * The asks a list of WAITING tasks does not already carry, and how many there are in all — the outreach-only
 * questions' rule (common-lib `asksNotOnTasks`): drop an ask whose decision is some waiting task's question, and
 * count `max(counted kept, (total ?? counted list) − dropped)` — a chase ask below high stakes is listed, never counted.
 */
export function asksNotOnTasks(
  waiting: { decisionPrompts?: { decisionId: string }[] }[],
  asks: WebOpenAsk[] = [],
  total?: number,
): { asks: WebOpenAsk[]; count: number } {
  const onTasks = new Set(waiting.flatMap((task) => (task.decisionPrompts ?? []).map((prompt) => prompt.decisionId)));
  const kept = asks.filter((ask) => !ask.prompt || !onTasks.has(ask.prompt.decisionId));
  const dropped = asks.length - kept.length;
  const counted = asks.filter(countsTowardActionNeeded).length;
  const keptCounted = kept.filter(countsTowardActionNeeded).length;
  return { asks: kept, count: Math.max(keptCounted, (total ?? counted) - dropped) };
}

/** What a ledger / decision ask is, for BI: the id's prefix (`decision`, `held-send`, `card`, `conv`). */
export function askKind(ask: Pick<WebOpenAsk, 'id'>): string {
  const at = ask.id.indexOf(':');
  return at > 0 ? ask.id.slice(0, at) : ask.id;
}
